import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import {
  fetchItemList,
  searchItems,
  type StatusPhase,
  type WasserijItemListRow,
} from '../lib/wasserij-items';
import { STATUS_PHASES, ITEM_STATUS_COLORS } from '../lib/item-constants';
import { formatPackageLabel, formatExtrasCount } from '../lib/item-helpers';
import { formatCurrency, formatRelativeTime } from '../lib/format';

function isStatusPhase(value: string | null): value is StatusPhase {
  if (!value) return false;
  return STATUS_PHASES.some((p) => p.key === value);
}

export function ItemsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialPhase = searchParams.get('phase');
  const [selectedPhase, setSelectedPhase] = useState<StatusPhase | null>(
    isStatusPhase(initialPhase) ? initialPhase : null
  );
  const [items, setItems] = useState<WasserijItemListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<WasserijItemListRow[] | null>(null);

  // Load list whenever phase changes (and on mount)
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchItemList({ statusPhase: selectedPhase ?? undefined })
      .then((list) => {
        if (!cancelled) {
          setItems(list);
          setLoading(false);
        }
      })
      .catch((err: Error) => {
        if (!cancelled) {
          setError(err.message || 'Kon items niet laden');
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [selectedPhase]);

  // URL ↔ phase sync
  useEffect(() => {
    const current = searchParams.get('phase');
    if (selectedPhase && current !== selectedPhase) {
      setSearchParams((sp) => {
        sp.set('phase', selectedPhase);
        return sp;
      }, { replace: true });
    } else if (!selectedPhase && current) {
      setSearchParams((sp) => {
        sp.delete('phase');
        return sp;
      }, { replace: true });
    }
  }, [selectedPhase, searchParams, setSearchParams]);

  // Search debounce
  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setSearchResults(null);
      setSearching(false);
      return;
    }
    const controller = new AbortController();
    setSearching(true);
    const timer = setTimeout(() => {
      searchItems(trimmed, controller.signal)
        .then((results) => {
          setSearchResults(results);
          setSearching(false);
        })
        .catch((err: Error & { name?: string }) => {
          if (err.name === 'AbortError') return;
          setSearching(false);
          setSearchResults([]);
        });
    }, 300);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query]);

  const isSearching = query.trim().length >= 2;
  const displayItems = isSearching ? (searchResults ?? []) : items;

  const subtitle = useMemo(() => {
    if (isSearching) return 'Zoekt in alle primaire items';
    if (selectedPhase) {
      const phase = STATUS_PHASES.find((p) => p.key === selectedPhase);
      return `Items in fase: ${phase?.label ?? selectedPhase}`;
    }
    return 'Primaire items van de laatste 90 dagen';
  }, [isSearching, selectedPhase]);

  return (
    <>
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-white">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-xl font-semibold text-slate-900">Items</h1>
              <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>
            </div>
            <div className="relative flex-1 max-w-sm">
              <svg
                className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M21 21l-4.35-4.35M10 18a8 8 0 100-16 8 8 0 000 16z"
                />
              </svg>
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Zoek WI-nummer, product, klant, soort..."
                className="w-full pl-9 pr-9 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              {searching && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className="w-3.5 h-3.5 border-2 border-slate-200 border-t-indigo-600 rounded-full animate-spin" />
                </div>
              )}
            </div>
          </div>

          {/* Filter chips */}
          {!isSearching && (
            <div className="mt-3 flex flex-wrap items-center gap-1.5">
              <button
                type="button"
                onClick={() => setSelectedPhase(null)}
                className={clsx(
                  'text-xs px-2.5 py-1 rounded-full border transition-colors',
                  selectedPhase === null
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                )}
              >
                Alle
              </button>
              {STATUS_PHASES.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setSelectedPhase(p.key)}
                  className={clsx(
                    'text-xs px-2.5 py-1 rounded-full border transition-colors',
                    selectedPhase === p.key
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                  )}
                >
                  {p.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-6 space-y-2 animate-pulse">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="h-12 bg-slate-100 rounded" />
              ))}
            </div>
          ) : error ? (
            <div className="p-6">
              <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            </div>
          ) : displayItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-400 gap-2">
              <p className="text-sm">
                {isSearching
                  ? `Geen items gevonden voor "${query}"`
                  : 'Geen items in deze fase'}
              </p>
              {isSearching && (
                <button
                  onClick={() => setQuery('')}
                  className="text-xs text-indigo-600 hover:underline"
                >
                  Zoekopdracht wissen
                </button>
              )}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-500 uppercase tracking-wide bg-slate-50 sticky top-0">
                <tr>
                  <th className="text-left font-medium px-6 py-2">Itemnr</th>
                  <th className="text-left font-medium px-4 py-2">Product</th>
                  <th className="text-left font-medium px-4 py-2">Soort</th>
                  <th className="text-left font-medium px-4 py-2">Afmeting</th>
                  <th className="text-right font-medium px-4 py-2">m²</th>
                  <th className="text-left font-medium px-4 py-2">Order</th>
                  <th className="text-left font-medium px-4 py-2">Klant</th>
                  <th className="text-left font-medium px-4 py-2">Pakket</th>
                  <th className="text-left font-medium px-4 py-2">Extra's</th>
                  <th className="text-left font-medium px-4 py-2">Status</th>
                  <th className="text-center font-medium px-4 py-2">Voortgang</th>
                  <th className="text-right font-medium px-4 py-2">Totaal</th>
                  <th className="text-left font-medium px-4 py-2">Leeftijd</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {displayItems.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() => navigate(`/items/${row.id}`)}
                    className="cursor-pointer hover:bg-slate-50 transition-colors"
                  >
                    <td className="px-6 py-3 font-medium text-slate-900">
                      {row.itemNumber || '—'}
                    </td>
                    <td className="px-4 py-3 text-slate-700">{row.productName || '—'}</td>
                    <td className="px-4 py-3 text-slate-600">{row.soort || '—'}</td>
                    <td className="px-4 py-3 text-slate-600">{row.dimensionsLabel}</td>
                    <td className="px-4 py-3 text-right text-slate-600">
                      {row.m2 != null ? row.m2.toFixed(2) : '—'}
                    </td>
                    <td className="px-4 py-3 text-slate-700">{row.orderNumber || '—'}</td>
                    <td className="px-4 py-3 text-slate-700">{row.customerName}</td>
                    <td className="px-4 py-3 text-slate-600">{formatPackageLabel(row.packageLabel)}</td>
                    <td className="px-4 py-3 text-slate-600">{formatExtrasCount(row.extrasCount)}</td>
                    <td className="px-4 py-3">
                      <span
                        className={clsx(
                          'text-[10px] px-1.5 py-0.5 rounded font-medium',
                          ITEM_STATUS_COLORS[row.status] || 'bg-slate-100 text-slate-600'
                        )}
                      >
                        {row.status || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-xs">
                      <span
                        className={clsx(
                          'inline-block mr-1',
                          row.voorinspectieGedaan ? 'text-emerald-600' : 'text-slate-300'
                        )}
                        title="Voorinspectie gedaan"
                      >
                        {row.voorinspectieGedaan ? '✓' : '○'}
                      </span>
                      <span
                        className={clsx(
                          row.klaarVoorAflevering ? 'text-emerald-600' : 'text-slate-300'
                        )}
                        title="Klaar voor aflevering"
                      >
                        {row.klaarVoorAflevering ? '✓' : '○'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-slate-900">
                      {row.totalInclBtw > 0 ? formatCurrency(row.totalInclBtw) : '—'}
                    </td>
                    <td className="px-4 py-3 text-slate-500">
                      {formatRelativeTime(row.createdDate)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}
